import { useActionMutation } from '../../feedback/useActionMutation';
import { useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OrderStatus } from '@palihog/shared';
import { supabase } from '../../lib/supabase';
import { adminCancellationErrorMessage } from './adminCancellation';

interface CancelOrderTarget {
  id: string;
  public_id: number;
  status: OrderStatus;
}

export function AdminCancelOrderDialog({ order, close }: { order: CancelOrderTarget; close: () => void }) {
  const client = useQueryClient();
  const [reason, setReason] = useState('');
  const requestKey = useRef(crypto.randomUUID());
  const orderReference = `PH-${String(order.public_id).padStart(5, '0')}`;
  const cancelOrder = useActionMutation('order.cancel', {
    mutationFn: async () => {
      const normalizedReason = reason.trim();
      if (!normalizedReason) throw new Error('Enter a cancellation reason before continuing.');
      const { error } = await supabase.rpc('admin_cancel_order', {
        target_order_id: order.id,
        cancellation_reason: normalizedReason,
        request_key: requestKey.current,
      });
      if (error) throw error;

      const { data: authoritativeOrder, error: readbackError } = await supabase
        .from('orders')
        .select('id,status,version,updated_at')
        .eq('id', order.id)
        .single();
      if (readbackError) throw new Error(`Cancellation was submitted, but the order could not be refreshed: ${readbackError.message}`);
      if (authoritativeOrder.status !== 'cancelled') {
        throw new Error('Cancellation was not confirmed by the authoritative order record. Refresh before trying again.');
      }
      return authoritativeOrder;
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['orders'] }),
        client.invalidateQueries({ queryKey: ['dispatch'] }),
        client.invalidateQueries({ queryKey: ['audit'] }),
      ]);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (cancelOrder.isPending || cancelOrder.isSuccess) return;
    cancelOrder.mutate();
  };

  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => !cancelOrder.isPending && event.target === event.currentTarget && close()}>
    <section className="modal" role="dialog" aria-modal="true" aria-labelledby="admin-cancel-title">
      <div className="section-heading"><div><p className="eyebrow">Manual override</p><h2 id="admin-cancel-title">Cancel order {orderReference}</h2></div><button className="icon-button" onClick={close} disabled={cancelOrder.isPending} aria-label="Close">×</button></div>
      {cancelOrder.isSuccess ? <div className="stack"><p className="alert success" role="status">Order {orderReference} was cancelled and confirmed by the authoritative order record.</p><p className="fine-print">The approved quote, financial snapshot, and complete order history were preserved. Dispatch can no longer start for this order.</p><div className="button-row end"><button className="button primary" onClick={close}>Close</button></div></div> : <form className="stack" onSubmit={submit}>
        <p>This permanently closes the unassigned order. It will not delete the order, approved quote, amounts, or audit history, and it will prevent later dispatch.</p>
        <p className="alert warning">Do not continue if a rider is assigned, an offer is pending, or any purchase/payment activity exists. The server will recheck all of these conditions before cancellation.</p>
        <label>Audited cancellation reason<textarea value={reason} onChange={(event) => setReason(event.target.value)} required maxLength={500} placeholder="Explain why this order must be cancelled" /></label>
        {cancelOrder.isError && <p className="alert error" role="alert">{adminCancellationErrorMessage(cancelOrder.error)}</p>}
        <div className="button-row end"><button type="button" className="button subtle" onClick={close} disabled={cancelOrder.isPending}>Keep order</button><button className="button danger" disabled={!reason.trim() || cancelOrder.isPending}>{cancelOrder.isPending ? 'Cancelling…' : `Cancel ${orderReference}`}</button></div>
      </form>}
    </section>
  </div>;
}
